import {Component, OnInit} from '@angular/core';
import {Select} from "@ngxs/store";
import {MatDialog} from "@angular/material/dialog";
import {UntilDestroy} from "@ngneat/until-destroy";
import {firstValueFrom, Observable} from "rxjs";
import {TranslateService} from "@ngx-translate/core";
import {Organization} from "app/models/organization.model";
import {OrganizationsState} from "app/state-management/states/organizations.states";
import {DisplayedProduct, Group, RelatedProductsProgram} from "app/models/menu.model";
import {DishesService} from "app/services/dishes.service";
import {ProductApiService} from "app/services/product-api.service";
import {RpProgramsService} from "app/services/rp-programs.service";
import {
  SelectProductsModalComponent,
  SelectProductsModalComponentDataType
} from "app/pages/menu/shared/select-products-modal/select-products-modal.component";

/** Служебное имя программы «для заказа»: в интерфейсе имени нет, бэку что-то отдать надо. */
const FOR_ORDER_PROGRAM_NAME = 'ForOrder';

/**
 * Вкладка «Сопутствующие товары к заказу».
 *
 * Здесь живёт ровно одна программа — `type: 'ForOrder'`, её киоск показывает в
 * корзине. Программы `ForProduct` (и ручные, и приехавшие из iiko) в эту вкладку
 * не попадают: они привязаны к блюдам и редактируются в карточке блюда.
 *
 * Вкладка не подписана на стор страницы меню (как и «Промо») — данные берёт
 * сама, поэтому работает независимо от того, открывали ли меню в этой сессии.
 */
@UntilDestroy()
@Component({
  selector: 'app-rp-programs',
  templateUrl: './rp-programs.component.html',
  styleUrls: ['./rp-programs.component.scss']
})
export class RpProgramsComponent implements OnInit {
  organization: Organization;
  @Select(OrganizationsState.getSelectedOrganization) selectedOrganization$: Observable<Organization>;

  loading = false;
  loadFailed = false;
  saveFailed = false;

  menuId = '';
  program: RelatedProductsProgram | null = null;
  /** Текущий список блюд: до «Применить» может отличаться от сохранённого. */
  dishes: string[] = [];
  products: DisplayedProduct[] = [];
  dictionaryProducts: Map<string, DisplayedProduct> = new Map<string, DisplayedProduct>();
  groups: Group[] = [];

  constructor(
    public dialog: MatDialog,
    private rpProgramsService: RpProgramsService,
    private productApiService: ProductApiService,
    private dishesService: DishesService,
    private translate: TranslateService,
  ) {
  }

  async ngOnInit(): Promise<void> {
    this.organization = await firstValueFrom(this.selectedOrganization$);
    this.menuId = await this.dishesService.getMenuIdByOrganization(this.organization.id);
    this.loading = true;
    this.loadFailed = false;
    try {
      await Promise.all([
        this.loadProgram(),
        this.loadProducts(),
        this.loadGroups(),
      ]);
      // Программа может ссылаться на блюда, которых в меню уже нет.
      this.dishes = this.dishes.filter(id => this.dictionaryProducts.has(id));
      this.draw();
    } catch (error) {
      console.error('Error loading order related products:', error);
      this.loadFailed = true;
    } finally {
      this.loading = false;
    }
  }

  /** Есть ли что показывать: программа создана. */
  get hasProgram(): boolean {
    return !!this.program;
  }

  async loadProgram(): Promise<void> {
    const programs = await this.rpProgramsService.getByMenu(this.menuId, 'ForOrder');
    // На меню такая программа одна; если бэк вернул несколько — берём первую.
    this.program = programs.length > 0 ? programs[0] : null;
    this.dishes = [...(this.program?.products ?? [])];
  }

  async loadProducts(): Promise<void> {
    const products = await this.productApiService.getProducts(this.menuId);
    products.forEach(product => this.dictionaryProducts.set(product.externalId, product));
  }

  /** Создание: сначала выбор блюд, потом один запрос — без промежуточной пустой программы. */
  create(): void {
    this.openDishesDialog(async selectedIds => {
      this.saveFailed = false;
      this.loading = true;
      try {
        const created = await this.rpProgramsService.create({
          menuId: this.menuId,
          type: 'ForOrder',
          name: FOR_ORDER_PROGRAM_NAME,
          products: selectedIds,
        });

        if (!created?.id) {
          this.saveFailed = true;
          return;
        }

        this.program = created;
        this.dishes = [...selectedIds];
        this.draw();
      } finally {
        this.loading = false;
      }
    });
  }

  editDishesList(): void {
    this.openDishesDialog(async selectedIds => {
      this.dishes = [...selectedIds];
      this.draw();
    });
  }

  async submit(): Promise<void> {
    if (!this.program) {
      return;
    }

    this.saveFailed = false;
    this.loading = true;
    try {
      const updated = await this.rpProgramsService.update(this.program.id, {
        menuId: this.menuId,
        type: 'ForOrder',
        name: FOR_ORDER_PROGRAM_NAME,
        products: this.dishes,
      });

      if (!updated?.id) {
        this.saveFailed = true;
        return;
      }

      this.program = updated;
    } finally {
      this.loading = false;
    }
  }

  async remove(): Promise<void> {
    if (!this.program) {
      return;
    }
    if (!confirm(this.translate.instant('MENU_EDIT.RP_PROGRAM.DELETE_CONFIRM'))) {
      return;
    }

    this.saveFailed = false;
    this.loading = true;
    try {
      await this.rpProgramsService.delete(this.program.id);
      this.program = null;
      this.dishes = [];
      this.draw();
    } finally {
      this.loading = false;
    }
  }

  draw(): void {
    this.products = [];
    for (const dish of this.dishes) {
      const product = this.dictionaryProducts.get(dish);
      if (product) {
        this.products.push(product);
      }
    }
  }

  private openDishesDialog(onPicked: (selectedIds: string[]) => Promise<void>): void {
    this.dialog.open(SelectProductsModalComponent, {
      data: {
        title: this.translate.instant('MENU_EDIT.RP_PROGRAM.SELECT_DISHES'),
        productIds: this.dishes,
        products: this.dictionaryProducts,
        groups: this.groups,
        organization: this.organization,
      } as SelectProductsModalComponentDataType
    }).afterClosed().subscribe(async result => {
      if (result) {
        await onPicked(result.data as string[]);
      }
    });
  }

  private async loadGroups(): Promise<void> {
    const response = await firstValueFrom(this.dishesService.getGroupIncludeAllList(this.menuId));
    this.groups = response.data;
  }
}
